import { useMemo, useState } from 'react';
import type { MediaAsset, Screen, VideoWallDetail } from '@ghostscreen/contracts';
import { useTranslation } from 'react-i18next';
import {
  WALL_MODE_OPTIONS,
  formatBezelMm,
  tileConnectionColor,
  tileConnectionLabel,
  tileIndex,
  tileLabel,
} from './wallFormat';

type WallCanvasCardProps = {
  wall: VideoWallDetail;
  screens: Screen[];
  mediaAssets: MediaAsset[];
  mediaAssetId: string;
  onMediaAssetIdChange: (id: string) => void;
  onModeChange: (mode: 'extend' | 'mirror') => void;
  onBezelChange: (mm: number) => void;
  onAssignTile: (cell: { row: number; col: number }, screenId: string) => void;
  onClearTile: (cell: { row: number; col: number }) => void;
  busy?: boolean;
};

export function WallCanvasCard({
  wall,
  screens,
  mediaAssets,
  mediaAssetId,
  onMediaAssetIdChange,
  onModeChange,
  onBezelChange,
  onAssignTile,
  onClearTile,
  busy = false,
}: WallCanvasCardProps) {
  const { t } = useTranslation('videoWalls');
  const [selected, setSelected] = useState<{ row: number; col: number } | null>(null);
  const [pickScreenId, setPickScreenId] = useState('');

  const tileByCell = useMemo(() => {
    const map = new Map<string, VideoWallDetail['tiles'][number]>();
    for (const tile of wall.tiles) map.set(`${tile.row}:${tile.col}`, tile);
    return map;
  }, [wall.tiles]);

  const assignedIds = new Set(wall.tiles.map((tile) => tile.screenId));
  const available = screens.filter((screen) => !assignedIds.has(screen.id));
  const contentName =
    mediaAssets.find(
      (asset) => asset.id === (mediaAssetId || wall.activePublication?.sourceMediaAssetId),
    )?.originalFilename ?? null;

  return (
    <article
      className="overflow-hidden rounded-[14px] border"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      <div
        className="flex flex-wrap items-center gap-3 border-b px-[22px] py-[17px]"
        style={{ borderColor: 'var(--gs-line)' }}
      >
        <span className="flex min-w-0 flex-col gap-[3px]">
          <span className="text-base font-bold">{wall.name}</span>
          <span className="text-[12.5px]" style={{ color: 'var(--gs-muted)' }}>
            {t('canvas.summary', {
              cols: wall.cols,
              rows: wall.rows,
              assigned: wall.tiles.length,
              count: wall.rows * wall.cols,
            })}
            {contentName ? ` · ${contentName}` : ''}
          </span>
        </span>
        <span className="ms-auto flex flex-wrap gap-2">
          {WALL_MODE_OPTIONS.map((option) => {
            const active = wall.mode === option.value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={busy}
                onClick={() => onModeChange(option.value)}
                className="rounded-full px-3.5 py-1.5 text-sm font-semibold disabled:opacity-50"
                style={{
                  background: active ? 'var(--gs-accent-solid)' : 'var(--gs-panel2)',
                  color: active ? 'var(--gs-accent-ink)' : 'var(--gs-ink2)',
                  border: active ? 'none' : '1px solid var(--gs-line)',
                  fontFamily: 'inherit',
                }}
              >
                {t(`modes.${option.value}.label`)}
              </button>
            );
          })}
        </span>
      </div>

      <div className="flex flex-col gap-4 px-[22px] py-[22px]">
        <div
          className="grid place-items-center rounded-xl border p-[22px]"
          style={{ background: '#0D0D0F', borderColor: 'var(--gs-line)' }}
        >
          <div
            className="grid w-full max-w-[460px] gap-1.5"
            dir="ltr"
            style={{
              aspectRatio: `${wall.cols} / ${wall.rows}`,
              gridTemplateColumns: `repeat(${wall.cols}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${wall.rows}, minmax(0, 1fr))`,
            }}
          >
            {Array.from({ length: wall.rows }, (_, row) =>
              Array.from({ length: wall.cols }, (_, col) => {
                const tile = tileByCell.get(`${row}:${col}`);
                const screenId = tile?.screenId;
                const screen = screens.find((item) => item.id === screenId);
                const active = selected?.row === row && selected?.col === col;
                const index = tileIndex(row, col, wall.cols);
                return (
                  <button
                    key={`${row}-${col}`}
                    type="button"
                    onClick={() => setSelected({ row, col })}
                    className="relative overflow-hidden rounded-md border text-start"
                    style={{
                      borderColor: active ? 'var(--gs-accent)' : 'rgba(255,255,255,0.12)',
                      background: 'rgba(255,255,255,0.04)',
                      fontFamily: 'inherit',
                    }}
                  >
                    <span className="relative flex flex-col gap-0.5 px-2.5 py-2">
                      <span className="text-[11px] font-bold tracking-[0.08em] text-white/90">
                        {t('canvas.tileBadge', { index })}
                      </span>
                      <span className="text-[10.5px] text-white/70">
                        {screen?.name ?? tileLabel(row, col, wall.rows, wall.cols)}
                      </span>
                    </span>
                    <span
                      className="absolute end-2 top-2 h-[7px] w-[7px] rounded-full"
                      style={{
                        background: tile ? tileConnectionColor(tile.connection) : 'rgba(255,255,255,0.35)',
                      }}
                      title={
                        tile
                          ? t('canvas.tileTooltip', {
                              connection: tileConnectionLabel(tile.connection),
                              ready: tile.ready ? t('canvas.tileReady') : t('canvas.tileNotReady'),
                              role:
                                tile.role === 'leader'
                                  ? t('canvas.roleLeader')
                                  : t('canvas.roleFollowerMuted'),
                            })
                          : t('canvas.unassigned')
                      }
                    />
                    {screenId ? (
                      <button
                        type="button"
                        className="absolute bottom-1.5 end-2 text-[10.5px] font-semibold text-white/60"
                        onClick={(event) => {
                          event.stopPropagation();
                          onClearTile({ row, col });
                        }}
                      >
                        {t('canvas.clear')}
                      </button>
                    ) : null}
                  </button>
                );
              }),
            )}
          </div>
        </div>

        {selected ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-2 text-[12.5px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
              {t('canvas.assignLabel', {
                position: tileLabel(selected.row, selected.col, wall.rows, wall.cols),
              })}
              <select
                value={pickScreenId}
                onChange={(event) => setPickScreenId(event.target.value)}
                className="min-w-[200px] rounded-[10px] border px-3 py-2 text-sm font-semibold outline-none"
                style={{
                  background: 'var(--gs-field)',
                  borderColor: 'var(--gs-line)',
                  color: 'var(--gs-ink)',
                  fontFamily: 'inherit',
                }}
              >
                <option value="">{t('canvas.selectScreen')}</option>
                {available.map((screen) => (
                  <option key={screen.id} value={screen.id}>
                    {screen.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={!pickScreenId || busy}
              onClick={() => {
                onAssignTile(selected, pickScreenId);
                setPickScreenId('');
              }}
              className="rounded-[10px] px-3 py-2 text-sm font-bold disabled:opacity-50"
              style={{ background: 'var(--gs-accent-solid)', color: 'var(--gs-accent-ink)' }}
            >
              {t('canvas.assign')}
            </button>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-7 gap-y-5">
          <label className="flex min-w-[210px] flex-1 flex-col gap-2">
            <span
              className="flex justify-between text-[12.5px] font-bold"
              style={{ color: 'var(--gs-ink2)' }}
            >
              {t('canvas.bezel')}
              <span className="font-mono font-semibold" style={{ color: 'var(--gs-muted)' }}>
                {formatBezelMm(wall.bezelHorizontalMm)}
              </span>
            </span>
            <input
              type="range"
              min={0}
              max={40}
              step={1}
              value={wall.bezelHorizontalMm}
              disabled={busy}
              onChange={(event) => onBezelChange(Number(event.target.value))}
              className="w-full cursor-pointer accent-[var(--gs-accent-solid)]"
            />
          </label>
          <span className="flex flex-col gap-1">
            <span
              className="text-xs font-bold uppercase tracking-[0.14em]"
              style={{ color: 'var(--gs-muted)' }}
            >
              {t('canvas.content')}
            </span>
            <span className="text-[13.5px] font-semibold">{contentName ?? t('canvas.noContent')}</span>
          </span>
          <span className="flex flex-col gap-1">
            <span
              className="text-xs font-bold uppercase tracking-[0.14em]"
              style={{ color: 'var(--gs-muted)' }}
            >
              {t('canvas.audio')}
            </span>
            <span className="text-[13.5px] font-semibold">{t('canvas.audioTileOnly', { index: 0 })}</span>
          </span>
        </div>

        <label className="flex flex-col gap-2 text-[12.5px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
          {t('canvas.publishAsset')}
          <select
            value={mediaAssetId}
            onChange={(event) => onMediaAssetIdChange(event.target.value)}
            className="w-full max-w-md rounded-[10px] border px-3 py-2 text-sm font-semibold outline-none"
            style={{
              background: 'var(--gs-field)',
              borderColor: 'var(--gs-line)',
              color: 'var(--gs-ink)',
              fontFamily: 'inherit',
            }}
          >
            <option value="">{t('canvas.selectReadyAsset')}</option>
            {mediaAssets.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.originalFilename}
              </option>
            ))}
          </select>
        </label>
      </div>
    </article>
  );
}
